import sanitizeHtml from "sanitize-html";
import { z } from "zod";
import { SignOffModeSchema } from "./sign-off-mode.ts";

/*
 * A revision's body and its ending are stored apart and joined when the draft is read
 * (`sign-offs.ts`, migration 038). `html`/`plainText` are the body alone. `signOff` is null for a
 * revision written before that migration, whose ending is already inside its body, and for one
 * whose campaign adds nothing; both read as body-only, which is what they are.
 *
 * `origin` distinguishes the three ways a revision comes about, so swapping an ending stays
 * legible in history as something other than a rewrite.
 */
export const DraftRevisionSchema = z.strictObject({
  revision: z.number().int().nonnegative(),
  origin: z.enum(["generated", "rep_edit", "sign_off"]),
  html: z.string().max(100_000),
  plainText: z.string().max(100_000),
  signOff: z
    .strictObject({
      mode: SignOffModeSchema,
      html: z.string().max(100_000),
      plainText: z.string().max(100_000),
    })
    .nullable(),
  /** The mailbox this run was sent from, so another ending can be composed for it later. */
  senderAddress: z.string().nullable(),
  actorId: z.string().nullable(),
  createdAt: z.string(),
});
export type DraftRevision = z.infer<typeof DraftRevisionSchema>;

export interface EmailText {
  readonly html: string;
  readonly plainText: string;
}

/** The draft as it will be sent: the model's body, then the sign-off as its own paragraph. */
export function withSignOff(body: EmailText, signOff: EmailText | null): EmailText {
  if (!signOff) return body;
  return {
    html: `${body.html}${signOff.html}`,
    plainText: `${body.plainText.trimEnd()}\n\n${signOff.plainText}`,
  };
}

/**
 * The whole email a revision represents. The body and the ending are stored apart and joined here.
 * The agent's own editor is handed the body alone, so an edit there never reaches the ending; the
 * inbox reply box takes this whole email, since it sends exactly what it holds, and "Change ending"
 * replaces the stored ending only while the box still ends with it.
 */
export function draftEmail(
  revision: Pick<DraftRevision, "html" | "plainText" | "signOff">,
): EmailText {
  const body = { html: revision.html, plainText: revision.plainText };
  return withSignOff(body, revision.signOff?.plainText ? revision.signOff : null);
}

export const ThreadSnapshotSchema = z.strictObject({
  /*
   * 2: each message's `html` is the email's own markup through `sanitizeInboundEmailHtml`, or its
   * linkified text when it has none. Absent: snapshots projected before 2026-09-18 hold escaped
   * plain text. Intake re-projects those in small batches (`#refreshStaleThreads`).
   */
  format: z.literal(2).optional(),
  subject: z.string(),
  campaignName: z.string(),
  workspaceName: z.string(),
  // A validated provider UI link is supplied by the host, never copied from email body text.
  bisonUrl: z.url().nullable(),
  messages: z.array(
    z.strictObject({
      id: z.string(),
      from: z.string(),
      to: z.array(z.string()),
      cc: z.array(z.string()),
      subject: z.string(),
      at: z.string(),
      direction: z.enum(["inbound", "outbound"]),
      html: z.string(),
      plainText: z.string(),
      attachments: z.array(
        z.strictObject({
          id: z.string(),
          name: z.string(),
          bytes: z.number().nonnegative().nullable(),
        }),
      ),
    }),
  ),
});
export type ThreadSnapshot = z.infer<typeof ThreadSnapshotSchema>;

const CLEAN_EMAIL_OPTIONS: sanitizeHtml.IOptions = {
  allowedTags: [
    "p",
    "br",
    "strong",
    "b",
    "em",
    "i",
    "u",
    "s",
    "a",
    "ul",
    "ol",
    "li",
    "blockquote",
    "div",
    "span",
    "table",
    "tbody",
    "tr",
    "td",
    "th",
    "hr",
  ],
  allowedAttributes: { a: ["href", "title", "target", "rel"] },
  allowedSchemes: ["https", "http", "mailto"],
  allowProtocolRelative: false,
  transformTags: {
    a: sanitizeHtml.simpleTransform("a", { target: "_blank", rel: "noopener noreferrer" }),
  },
};

/** Formatting only. No remote images, CSS, forms, script, or tracking loads. */
export function cleanEmailHtml(html: string): string {
  return sanitizeHtml(html, CLEAN_EMAIL_OPTIONS);
}

/**
 * `cleanEmailHtml`, also removing every element whose whole text `drop` accepts, with its content.
 * An element's text includes its children's, so a wrapper holding other text survives while the
 * paragraph holding only the dropped words goes.
 */
export function cleanEmailHtmlDropping(html: string, drop: (text: string) => boolean): string {
  return sanitizeHtml(html, {
    ...CLEAN_EMAIL_OPTIONS,
    exclusiveFilter: (frame) => frame.text.trim() !== "" && drop(frame.text),
  });
}

/*
 * Inbound email for display inside the sandboxed reading frame (`email-frame.ts`). This is the
 * first of three independent layers; the frame's sandbox and its Content-Security-Policy are the
 * other two, and each is tested on its own.
 *
 * Kept: the formatting mail actually uses (tables, inline styles, fonts, colours, lists) and links.
 * Removed: script, event handlers, forms, frames, embedded objects, style blocks, and any URL that
 * is not absolute http(s) or mailto, which includes `javascript:` and every relative URL: a
 * relative URL inside a same-origin frame would resolve against this application. CSS values that
 * can fetch (`url()`, `image-set()`, `@import`) or contain escapes are dropped declaration by declaration.
 * Remote images keep their address only in `data-remote-src`, so nothing loads until the rep asks.
 */
const SAFE_CSS_VALUE =
  /^(?![\s\S]*(?:\\|url\s*\(|image-set\s*\(|image\s*\(|element\s*\(|expression\s*\(|@import|javascript:|behavior|binding))[\s\S]*$/i;
const EMAIL_STYLE_PROPERTIES = [
  "background",
  "background-color",
  "border",
  "border-bottom",
  "border-bottom-color",
  "border-bottom-style",
  "border-bottom-width",
  "border-collapse",
  "border-color",
  "border-left",
  "border-left-color",
  "border-left-style",
  "border-left-width",
  "border-radius",
  "border-right",
  "border-right-color",
  "border-right-style",
  "border-right-width",
  "border-spacing",
  "border-style",
  "border-top",
  "border-top-color",
  "border-top-style",
  "border-top-width",
  "border-width",
  "clear",
  "color",
  "direction",
  "display",
  "float",
  "font",
  "font-family",
  "font-size",
  "font-style",
  "font-variant",
  "font-weight",
  "height",
  "letter-spacing",
  "line-height",
  "list-style",
  "list-style-position",
  "list-style-type",
  "margin",
  "margin-bottom",
  "margin-left",
  "margin-right",
  "margin-top",
  "max-height",
  "max-width",
  "min-height",
  "min-width",
  "overflow",
  "padding",
  "padding-bottom",
  "padding-left",
  "padding-right",
  "padding-top",
  "table-layout",
  "text-align",
  "text-decoration",
  "text-indent",
  "text-transform",
  "vertical-align",
  "white-space",
  "width",
  "word-break",
  "word-spacing",
  "word-wrap",
];
/**
 * Quote containers the frame can hide, by the markers Gmail, Apple Mail, Outlook, Yahoo and
 * Thunderbird write, and Gmail's signature wrapper, so the fold can say the author's signature is
 * behind it.
 */
const QUOTE_CLASSES = [
  "gmail_quote",
  "gmail_quote_container",
  "gmail_attr",
  "gmail_signature",
  "yahoo_quoted",
  "moz-cite-prefix",
];
const QUOTE_IDS = new Set(["appendonsend", "divRplyFwdMsg"]);

function absoluteUrl(value: string | undefined, schemes: readonly string[]): string | null {
  if (!value) return null;
  try {
    const url = new URL(value.trim());
    return schemes.includes(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
}

export function sanitizeInboundEmailHtml(html: string): string {
  return sanitizeHtml(html, {
    allowedTags: [
      "a",
      "abbr",
      "b",
      "big",
      "blockquote",
      "br",
      "caption",
      "center",
      "cite",
      "code",
      "col",
      "colgroup",
      "dd",
      "del",
      "div",
      "dl",
      "dt",
      "em",
      "font",
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      "hr",
      "i",
      "img",
      "ins",
      "kbd",
      "li",
      "mark",
      "ol",
      "p",
      "pre",
      "q",
      "s",
      "small",
      "span",
      "strike",
      "strong",
      "sub",
      "sup",
      "table",
      "tbody",
      "td",
      "tfoot",
      "th",
      "thead",
      "tr",
      "tt",
      "u",
      "ul",
    ],
    nonTextTags: ["script", "style", "textarea", "option", "noscript", "title", "head", "xml"],
    allowedAttributes: {
      "*": [
        "style",
        "align",
        "valign",
        "dir",
        "width",
        "height",
        "bgcolor",
        "class",
        "id",
        "title",
      ],
      a: ["href", "target", "rel"],
      img: ["src", "data-remote-src", "alt", "border"],
      blockquote: ["type"],
      td: ["colspan", "rowspan", "nowrap"],
      th: ["colspan", "rowspan", "nowrap"],
      col: ["span"],
      colgroup: ["span"],
      table: ["border", "cellpadding", "cellspacing"],
      font: ["color", "face", "size"],
      ol: ["start", "type"],
    },
    allowedClasses: { "*": QUOTE_CLASSES },
    allowedStyles: {
      "*": Object.fromEntries(
        EMAIL_STYLE_PROPERTIES.map((property) => [property, [SAFE_CSS_VALUE]]),
      ),
    },
    allowedSchemes: ["http", "https", "mailto"],
    allowedSchemesByTag: { img: ["data"] },
    allowProtocolRelative: false,
    transformTags: {
      a: (tagName, attribs) => {
        const { href: raw, target: _target, rel: _rel, ...rest } = attribs;
        const href = absoluteUrl(raw, ["http:", "https:", "mailto:"]);
        return {
          tagName,
          // A link with nowhere safe to go keeps its text and styling and loses its target.
          attribs: href ? { ...rest, href, target: "_blank", rel: "noopener noreferrer" } : rest,
        };
      },
      img: (tagName, attribs) => {
        const { src, ...rest } = attribs;
        const inline = src?.trim().toLowerCase().startsWith("data:image/") ? src.trim() : null;
        const remote = absoluteUrl(src, ["https:"]);
        const kept = Object.fromEntries(
          Object.entries(rest).filter(([name]) => name !== "data-remote-src"),
        );
        return {
          tagName,
          attribs: inline
            ? { ...kept, src: inline }
            : remote
              ? { ...kept, "data-remote-src": remote }
              : kept,
        };
      },
      "*": (tagName, attribs) => {
        const { id, ...rest } = attribs;
        return { tagName, attribs: id && QUOTE_IDS.has(id) ? { ...rest, id } : rest };
      },
    },
  });
}

export function emailPlainText(html: string): string {
  const separated = html.replace(/<(?:br\s*\/?|\/p|\/div|\/li|\/tr|\/blockquote)>/gi, "\n");
  return sanitizeHtml(separated, { allowedTags: [], allowedAttributes: {} })
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, " ")
    .trim();
}

/**
 * An edited draft body: blocks are paragraphs, while breaks and list items stay single lines.
 * Signatures use `emailPlainText` instead: their separate paragraphs are consecutive address lines.
 */
export function draftBodyPlainText(html: string): string {
  const parts: string[] = [];
  let lineBlockDepth = 0;
  let breaks = 0;
  const boundary = (name: string): void => {
    if (name === "br") breaks = Math.min(2, breaks + 1);
    else if (/^(?:p|div|blockquote|ul|ol|table|li|tr|hr)$/u.test(name))
      breaks = Math.max(breaks, lineBlockDepth > 0 ? 1 : 2);
  };
  sanitizeHtml(cleanEmailHtml(html), {
    allowedTags: [],
    allowedAttributes: {},
    onOpenTag(name) {
      if (name === "li" || name === "tr") lineBlockDepth += 1;
      boundary(name);
    },
    onCloseTag(name) {
      if (name !== "br") boundary(name);
      if (name === "li" || name === "tr") lineBlockDepth -= 1;
    },
    textFilter(text) {
      const words = text.replace(/\s+/gu, " ");
      if (words.trim() !== "") {
        if (parts.length && breaks) parts.push("\n".repeat(breaks));
        parts.push(words);
        breaks = 0;
      } else if (!breaks) parts.push(words);
      return "";
    },
  });
  return emailPlainText(parts.join("")).replace(/[ \t]*\n[ \t]*/gu, "\n");
}
